/**
 * Baseball Game - Main Component
 * Includes Home Run Derby (3D), Pitcher vs Batter Multiplayer, Achievements & Leaderboards
 */
import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { 
  ChevronLeft, Trophy, Target, Users, Zap, Copy, Check,
  Home, Award, Star, TrendingUp, Medal, Lock, Unlock, Crown, Loader2
} from 'lucide-react';
import BaseballField2D from './BaseballField2D';
import { useBaseballStore } from './baseballStore';
import { PITCH_TYPES, ACHIEVEMENTS, getRank, RANKED_TIERS } from './baseballConstants';
import { useAuth } from '@/contexts/AuthContext';

// Lazy load 3D components for better performance
const HomeRunDerby3D = lazy(() => import('./HomeRunDerbyVanilla'));

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== Derby Leaderboard ====================

const DerbyLeaderboard = ({ isOpen, onClose }) => {
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (isOpen) {
      fetchLeaderboard();
    }
  }, [isOpen]);
  
  const fetchLeaderboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/baseball/derby-leaderboard?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setScores(data);
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-amber-500/30 text-white max-w-md" data-testid="derby-leaderboard-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-400 flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Home Run Derby Leaderboard
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Top home run hitters in the derby
          </DialogDescription>
        </DialogHeader>
        
        <div className="max-h-[400px] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : scores.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No scores yet. Be the first!
            </div>
          ) : (
            <div className="space-y-2">
              {scores.map((score, idx) => (
                <div
                  key={idx}
                  className={`
                    flex items-center justify-between p-3 rounded-lg
                    ${idx === 0 ? 'bg-amber-500/20 border border-amber-500/30' :
                      idx === 1 ? 'bg-gray-400/10 border border-gray-400/20' :
                      idx === 2 ? 'bg-orange-600/10 border border-orange-500/20' :
                      'bg-slate-800/50'}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <span className={`
                      w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                      ${idx === 0 ? 'bg-amber-500 text-black' :
                        idx === 1 ? 'bg-gray-400 text-black' :
                        idx === 2 ? 'bg-orange-600 text-white' :
                        'bg-slate-700 text-gray-300'}
                    `}>
                      {score.rank}
                    </span>
                    <div>
                      <div className="font-semibold text-white">{score.user_name}</div>
                      <div className="text-xs text-gray-400">
                        {new Date(score.played_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-amber-400">{score.home_runs} HR</div>
                    <div className="text-xs text-gray-400">{score.total_distance} ft</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ==================== Achievements Panel ====================

const AchievementsPanel = ({ isOpen, onClose, achievements, playerStats }) => {
  const allAchievements = Object.values(ACHIEVEMENTS);
  const unlockedIds = achievements.map(a => a.id);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-purple-500/30 text-white max-w-lg" data-testid="achievements-dialog">
        <DialogHeader>
          <DialogTitle className="text-purple-400 flex items-center gap-2">
            <Award className="w-5 h-5" />
            Achievements & Power-ups
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Unlock achievements to earn power-ups and cosmetics
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 max-h-[400px] overflow-y-auto">
          {allAchievements.map((achievement) => {
            const unlocked = unlockedIds.includes(achievement.id);
            
            return (
              <div
                key={achievement.id}
                className={`
                  p-4 rounded-xl border transition-all
                  ${unlocked 
                    ? 'bg-purple-500/10 border-purple-500/30' 
                    : 'bg-slate-800/50 border-slate-700 opacity-60'}
                `}
              >
                <div className="flex items-start gap-4">
                  <div className={`
                    w-12 h-12 rounded-lg flex items-center justify-center text-2xl
                    ${unlocked ? 'bg-purple-500/20' : 'bg-slate-700'}
                  `}>
                    {unlocked ? achievement.icon : <Lock className="w-5 h-5 text-gray-500" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-white">{achievement.name}</h4>
                      {unlocked && <Unlock className="w-4 h-4 text-green-400" />}
                    </div>
                    <p className="text-sm text-gray-400">{achievement.description}</p>
                    {achievement.reward && (
                      <div className="mt-2 text-xs text-purple-400 flex items-center gap-1">
                        <Star className="w-3 h-3" />
                        Reward: {achievement.reward.type === 'bat_size' ? '+5% Bat Size' :
                                 achievement.reward.type === 'unlock_pitch' ? `Unlock ${achievement.reward.value}` :
                                 achievement.reward.type === 'power_swing' ? 'Power Swing x1' :
                                 achievement.reward.type === 'cosmetic' ? `${achievement.reward.value} Cosmetic` :
                                 achievement.reward.type === 'contact_boost' ? '+10% Contact' :
                                 'Special Reward'}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-4 pt-4 border-t border-slate-700">
          <div className="text-sm text-gray-400">
            Progress: {achievements.length} / {allAchievements.length} achievements unlocked
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ==================== Multiplayer Lobby ====================

const MultiplayerLobby = ({ isOpen, onClose, onJoinRoom }) => {
  const { user } = useAuth();
  const [mode, setMode] = useState('menu'); // menu, create, join
  const [playerName, setPlayerName] = useState(user?.name || '');
  const [roomId, setRoomId] = useState('');
  const [createdRoom, setCreatedRoom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  
  const createRoom = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/baseball/create-room`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ player_name: playerName || 'Player 1' })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCreatedRoom(data);
        setMode('waiting');
      } else {
        setError('Failed to create room');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };
  
  const joinRoom = async () => {
    if (!roomId.trim()) {
      setError('Enter a room code');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/baseball/room/${roomId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ player_name: playerName || 'Player 2' })
      });
      
      if (response.ok) {
        const data = await response.json();
        onJoinRoom(roomId, 'batter', data);
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to join room');
      }
    } catch (err) {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };
  
  const copyRoomCode = () => {
    if (createdRoom) {
      navigator.clipboard.writeText(createdRoom.room_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const startAsHost = () => {
    if (createdRoom) {
      onJoinRoom(createdRoom.room_id, 'pitcher', { host_name: playerName });
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-blue-500/30 text-white max-w-md" data-testid="multiplayer-lobby-dialog">
        <DialogHeader>
          <DialogTitle className="text-blue-400 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Pitcher vs Batter
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Challenge a friend to a 1v1 baseball duel
          </DialogDescription>
        </DialogHeader>
        
        {mode === 'menu' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Your Name</label>
              <Input
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Enter your name"
                className="bg-slate-800 border-slate-600"
                data-testid="player-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                onClick={() => setMode('create')}
                className="bg-blue-600 hover:bg-blue-700 h-20 flex-col"
                data-testid="create-game-btn"
              >
                <Target className="w-6 h-6 mb-1" />
                Create Game
              </Button>
              <Button
                onClick={() => setMode('join')}
                variant="outline"
                className="border-slate-600 h-20 flex-col"
                data-testid="join-game-btn"
              >
                <Users className="w-6 h-6 mb-1" />
                Join Game
              </Button>
            </div>
          </div>
        )}
        
        {mode === 'create' && !createdRoom && (
          <div className="space-y-4">
            <p className="text-gray-400 text-sm">
              Create a room and share the code with your opponent. You'll pitch first!
            </p>
            <Button
              onClick={createRoom}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Creating...' : 'Create Room'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => setMode('menu')}
              className="w-full"
            >
              Back
            </Button>
          </div>
        )}
        
        {mode === 'waiting' && createdRoom && (
          <div className="space-y-4 text-center">
            <div className="text-gray-400">Share this code with your opponent:</div>
            <div className="flex items-center gap-2 justify-center">
              <div className="text-3xl font-mono font-bold text-blue-400 bg-slate-800 px-4 py-2 rounded-lg">
                {createdRoom.room_id}
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={copyRoomCode}
                className="text-gray-400"
              >
                {copied ? <Check className="w-5 h-5 text-green-400" /> : <Copy className="w-5 h-5" />}
              </Button>
            </div>
            <div className="text-sm text-gray-500">Waiting for opponent to join...</div>
            <Button
              onClick={startAsHost}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              Start Game (Demo Mode)
            </Button>
          </div>
        )}
        
        {mode === 'join' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Room Code</label>
              <Input
                value={roomId}
                onChange={(e) => setRoomId(e.target.value.toUpperCase())}
                placeholder="Enter room code"
                className="bg-slate-800 border-slate-600 text-center text-xl font-mono"
              />
            </div>
            {error && <div className="text-red-400 text-sm">{error}</div>}
            <Button
              onClick={joinRoom}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Joining...' : 'Join Game'}
            </Button>
            <Button
              variant="ghost"
              onClick={() => { setMode('menu'); setError(''); }}
              className="w-full"
            >
              Back
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ==================== Pitcher vs Batter Game ====================

const PitcherVsBatter = ({ roomId, role, opponentName, onExit }) => {
  const [gameState, setGameState] = useState({
    inning: 1,
    outs: 0,
    score: { player: 0, opponent: 0 },
    currentBatter: role === 'batter' ? 'player' : 'opponent',
    status: 'playing'
  });
  
  const [selectedPitch, setSelectedPitch] = useState('FASTBALL');
  const [pitchTarget, setPitchTarget] = useState({ x: 0, y: 2.5 });
  const [currentPitch, setCurrentPitch] = useState(null);
  const [pitchInFlight, setPitchInFlight] = useState(false);
  const [swingResult, setSwingResult] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isMyTurn, setIsMyTurn] = useState(role === 'pitcher');
  const [ballPosition, setBallPosition] = useState([0, 2, -18]);
  const [swingAngle, setSwingAngle] = useState(20);
  const [isSwinging, setIsSwinging] = useState(false);
  const [ws, setWs] = useState(null);
  
  const pitchStartTime = useRef(null);
  const [connectionError, setConnectionError] = useState(null);
  
  // Connect WebSocket with timeout handling
  useEffect(() => {
    const wsUrl = API_URL.replace(/^https/, 'wss').replace(/^http:/, 'ws:');
    const socket = new WebSocket(`${wsUrl}/api/ws/baseball/${roomId}`);
    let connectionTimeout;
    
    // Set connection timeout
    connectionTimeout = setTimeout(() => {
      if (socket.readyState !== WebSocket.OPEN) {
        setConnectionError('Connection timed out. Please try again.');
        socket.close();
      }
    }, 8000);
    
    socket.onopen = () => {
      console.log('Connected to baseball room');
      clearTimeout(connectionTimeout);
      setConnectionError(null);
    };
    
    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      handleMessage(data);
    };
    
    socket.onclose = () => {
      console.log('Disconnected from room');
      clearTimeout(connectionTimeout);
    };
    
    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
      clearTimeout(connectionTimeout);
      setConnectionError('Connection failed. Please check your network.');
    };
    
    setWs(socket);
    
    return () => {
      clearTimeout(connectionTimeout);
      socket.close();
    };
  }, [roomId]);
  
  const handleMessage = (data) => {
    switch (data.type) {
      case 'connected':
        setGameState(prev => ({ ...prev, ...data.game_state }));
        break;
        
      case 'player_joined':
        // Opponent joined, game can start
        break;
        
      case 'pitch':
        // Received pitch from opponent (I'm batting)
        setCurrentPitch(data.pitch);
        setPitchInFlight(true);
        pitchStartTime.current = Date.now();
        setIsMyTurn(true);
        animatePitch(data.pitch);
        break;
        
      case 'swing_result':
        // Received swing result from opponent (I was pitching)
        setSwingResult(data.result);
        setShowResult(true);
        setPitchInFlight(false);
        updateScore(data.result);
        setTimeout(() => {
          setShowResult(false);
          setSwingResult(null);
          setIsMyTurn(true);
        }, 2000);
        break;
        
      case 'inning_change':
        setGameState(prev => ({
          ...prev,
          inning: data.inning,
          outs: 0,
          currentBatter: data.current_batter
        }));
        break;
        
      case 'game_over':
        setGameState(prev => ({
          ...prev,
          status: 'ended',
          score: data.finalScore
        }));
        break;
        
      default:
        break;
    }
  };
  
  const animatePitch = (pitch) => {
    const startZ = -18;
    const endZ = 0;
    const duration = 600;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const z = startZ + (endZ - startZ) * progress * progress;
      
      const movement = PITCH_TYPES[pitch.type]?.movement || { x: 0, y: 0 };
      const x = (pitch.targetX || 0) + (typeof movement.x === 'number' ? movement.x * progress : (Math.random() - 0.5) * 2);
      const y = 2.5 + (pitch.targetY - 2.5 || 0) + (typeof movement.y === 'number' ? movement.y * progress : (Math.random() - 0.5));
      
      setBallPosition([x, y, z]);
      
      if (progress < 1 && pitchInFlight) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  };
  
  const throwPitch = () => {
    if (!ws || role !== 'pitcher' || !isMyTurn) return;
    
    const pitch = {
      type: selectedPitch,
      targetX: pitchTarget.x,
      targetY: pitchTarget.y,
      speed: PITCH_TYPES[selectedPitch].speed
    };
    
    ws.send(JSON.stringify({ type: 'pitch', pitch }));
    setCurrentPitch(pitch);
    setPitchInFlight(true);
    pitchStartTime.current = Date.now();
    setIsMyTurn(false);
    animatePitch(pitch);
  };
  
  const swing = () => {
    if (!ws || role !== 'batter' || !isMyTurn || !pitchInFlight) return;
    
    const elapsed = Date.now() - pitchStartTime.current;
    const optimalTime = 500;
    const timing = elapsed - optimalTime;
    
    const result = calculateSwingResult(timing, swingAngle);
    
    ws.send(JSON.stringify({ type: 'swing', timing, swingAngle, result }));
    
    setIsSwinging(true);
    setTimeout(() => setIsSwinging(false), 300);
    
    setSwingResult(result);
    setShowResult(true);
    setPitchInFlight(false);
    
    setTimeout(() => {
      setShowResult(false);
      setSwingResult(null);
      updateScore(result);
    }, 2000);
  };
  
  const calculateSwingResult = (timing, angle) => {
    const absTime = Math.abs(timing);
    let quality = 'miss';
    if (absTime <= 50) quality = 'perfect';
    else if (absTime <= 100) quality = 'good';
    else if (absTime <= 150) quality = 'okay';
    
    if (quality === 'miss') {
      return { result: 'STRIKE', distance: 0, exitVelocity: 0 };
    }
    
    const baseVelocity = quality === 'perfect' ? 105 : quality === 'good' ? 95 : 80;
    const velocity = baseVelocity + (Math.random() - 0.5) * 15;
    const launchAngle = angle + (timing > 0 ? 5 : -5) + (Math.random() - 0.5) * 10;
    
    const distance = (velocity * velocity * Math.sin(2 * launchAngle * Math.PI / 180)) / 9.8 * 3.28 * 0.4;
    
    let result = 'OUT';
    if (distance >= 330 && launchAngle >= 20 && launchAngle <= 40) result = 'HOMERUN';
    else if (distance >= 280) result = Math.random() > 0.4 ? 'TRIPLE' : 'DOUBLE';
    else if (distance >= 220) result = Math.random() > 0.5 ? 'DOUBLE' : 'SINGLE';
    else if (distance >= 150) result = Math.random() > 0.6 ? 'SINGLE' : 'OUT';
    
    return { result, distance: Math.round(distance), exitVelocity: Math.round(velocity), launchAngle: Math.round(launchAngle) };
  };
  
  const updateScore = (result) => {
    if (result.result === 'STRIKE' || result.result === 'OUT') {
      setGameState(prev => {
        const newOuts = prev.outs + 1;
        if (newOuts >= 3) {
          // Switch sides
          return {
            ...prev,
            outs: 0,
            inning: prev.currentBatter === 'opponent' ? prev.inning + 1 : prev.inning,
            currentBatter: prev.currentBatter === 'player' ? 'opponent' : 'player'
          };
        }
        return { ...prev, outs: newOuts };
      });
    } else {
      const runs = { SINGLE: 0.25, DOUBLE: 0.5, TRIPLE: 0.75, HOMERUN: 1 }[result.result] || 0;
      setGameState(prev => ({
        ...prev,
        score: {
          ...prev.score,
          [prev.currentBatter]: prev.score[prev.currentBatter] + runs
        }
      }));
    }
  };
  
  // Check game end
  useEffect(() => {
    if (gameState.inning > 3) {
      setGameState(prev => ({ ...prev, status: 'ended' }));
    }
  }, [gameState.inning]);
  
  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (role === 'pitcher' && isMyTurn && !pitchInFlight) {
          throwPitch();
        } else if (role === 'batter' && pitchInFlight) {
          swing();
        }
      }
      if (role === 'batter') {
        if (e.code === 'ArrowUp') setSwingAngle(prev => Math.min(prev + 5, 45));
        if (e.code === 'ArrowDown') setSwingAngle(prev => Math.max(prev - 5, -10));
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [role, isMyTurn, pitchInFlight]);
  
  if (gameState.status === 'ended') {
    const playerWon = gameState.score.player > gameState.score.opponent;
    
    return (
      <div className="h-full flex items-center justify-center bg-slate-900">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-slate-800 border border-blue-500/30 rounded-2xl p-8 text-center"
        >
          <Crown className={`w-16 h-16 mx-auto mb-4 ${playerWon ? 'text-amber-400' : 'text-gray-500'}`} />
          <h2 className="text-3xl font-bold text-white mb-2">
            {playerWon ? 'Victory!' : 'Defeat'}
          </h2>
          <div className="text-2xl text-gray-300 mb-6">
            {Math.round(gameState.score.player)} - {Math.round(gameState.score.opponent)}
          </div>
          <Button onClick={onExit} className="bg-blue-600 hover:bg-blue-700">
            Return to Menu
          </Button>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="relative w-full h-full" data-testid="pitcher-vs-batter-game">
      {/* Connection Error */}
      {connectionError && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-30 bg-red-600/90 backdrop-blur-sm rounded-xl p-4 flex items-center gap-3">
          <div className="text-white">{connectionError}</div>
          <Button
            size="sm"
            variant="outline"
            onClick={onExit}
            className="border-white/50 text-white hover:bg-white/10"
          >
            Return to Menu
          </Button>
        </div>
      )}
      
      {/* Field */}
      <BaseballField2D
        ballPosition={{ x: ballPosition[0], y: ballPosition[1], z: ballPosition[2] }}
        ballVisible={pitchInFlight || showResult}
        showStrikeZone={true}
        batterSwinging={isSwinging}
      />
      
      {/* Game HUD */}
      <div className="absolute top-4 left-4 bg-black/70 backdrop-blur-sm rounded-xl p-4">
        <div className="text-blue-400 text-sm mb-1">PITCHER VS BATTER</div>
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-white">Inning {gameState.inning}</div>
            <div className="text-xs text-gray-400">{gameState.outs} Outs</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-400">{Math.round(gameState.score.player)}</div>
            <div className="text-xs text-gray-400">You</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-red-400">{Math.round(gameState.score.opponent)}</div>
            <div className="text-xs text-gray-400">Opponent</div>
          </div>
        </div>
      </div>
      
      {/* Role indicator */}
      <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm rounded-xl p-4">
        <div className={`text-sm font-bold ${role === 'pitcher' ? 'text-blue-400' : 'text-amber-400'}`}>
          You are: {role === 'pitcher' ? 'PITCHING' : 'BATTING'}
        </div>
        <div className="text-xs text-gray-400">
          {isMyTurn ? 'Your turn!' : 'Waiting...'}
        </div>
      </div>
      
      {/* Pitcher controls */}
      {role === 'pitcher' && isMyTurn && !pitchInFlight && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
          <div className="bg-black/70 backdrop-blur-sm rounded-xl p-4">
            <div className="text-sm text-gray-400 mb-2">Select Pitch</div>
            <div className="flex gap-2">
              {Object.keys(PITCH_TYPES).slice(0, 4).map(type => (
                <button
                  key={type}
                  onClick={() => setSelectedPitch(type)}
                  className={`
                    px-3 py-2 rounded-lg text-xs font-bold transition-all
                    ${selectedPitch === type 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-700 text-gray-300 hover:bg-slate-600'}
                  `}
                >
                  {PITCH_TYPES[type].name}
                </button>
              ))}
            </div>
          </div>
          <Button
            onClick={throwPitch}
            className="bg-blue-600 hover:bg-blue-700 px-8 py-6 text-lg"
          >
            <Target className="w-5 h-5 mr-2" />
            Throw Pitch (Space)
          </Button>
        </div>
      )}
      
      {/* Batter controls */}
      {role === 'batter' && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
          <div className="bg-black/70 backdrop-blur-sm rounded-xl p-4">
            <div className="text-sm text-gray-400 mb-2">Swing Angle</div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setSwingAngle(prev => Math.max(prev - 5, -10))}
                className="w-8 h-8 rounded bg-slate-700 hover:bg-slate-600 text-white"
              >
                ↓
              </button>
              <div className="text-2xl font-bold text-white w-12 text-center">{swingAngle}°</div>
              <button 
                onClick={() => setSwingAngle(prev => Math.min(prev + 5, 45))}
                className="w-8 h-8 rounded bg-slate-700 hover:bg-slate-600 text-white"
              >
                ↑
              </button>
            </div>
          </div>
          
          {pitchInFlight && (
            <Button
              onClick={swing}
              className="bg-green-600 hover:bg-green-700 px-12 py-8 text-xl animate-pulse"
            >
              <Zap className="w-6 h-6 mr-2" />
              SWING! (Space)
            </Button>
          )}
        </div>
      )}
      
      {/* Result popup */}
      <AnimatePresence>
        {showResult && swingResult && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none z-20"
          >
            <div className={`
              text-center px-8 py-6 rounded-2xl
              ${swingResult.result === 'HOMERUN' ? 'bg-gradient-to-r from-amber-500 to-orange-500' : 
               swingResult.result === 'STRIKE' ? 'bg-red-600' :
               swingResult.result === 'OUT' ? 'bg-gray-600' :
               'bg-green-600'}
            `}>
              <div className="text-4xl font-black text-white mb-2">
                {swingResult.result === 'HOMERUN' ? '💣 HOME RUN!' : swingResult.result}
              </div>
              {swingResult.distance > 0 && (
                <div className="text-xl text-white/90">
                  {swingResult.distance} ft | {swingResult.exitVelocity} mph
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==================== Game Mode Selection ====================

const GameModeSelector = ({ onSelectMode, onShowLeaderboard, onShowAchievements, playerStats, use3D, onToggle3D }) => {
  const rank = getRank(playerStats?.rankedPoints || 0);
  
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-obsidian z-20" data-testid="baseball-mode-selector">
      {/* Subtle pattern */}
      <div className="absolute inset-0 bg-dot-pattern opacity-30 pointer-events-none" />
      
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-obsidian-card border border-slate-800 rounded-xl p-8 max-w-lg w-full mx-4 relative z-10"
      >
        <h2 className="font-display text-3xl font-bold text-center text-white mb-2">Baseball</h2>
        <p className="text-slate-500 text-center mb-6 text-sm">Choose your game mode</p>
        
        <div className="space-y-3">
          <button
            onClick={() => onSelectMode('derby')}
            data-testid="home-run-derby-btn"
            className="w-full p-5 bg-obsidian border border-amber-500/20 rounded-xl 
                       hover:border-amber-500/40 transition-all group text-left"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center
                            group-hover:bg-amber-500/20 transition-colors">
                <Trophy className="w-6 h-6 text-amber-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-white">Home Run Derby</h3>
                  {use3D && (
                    <span className="text-[10px] px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full font-medium">
                      3D
                    </span>
                  )}
                </div>
                <p className="text-slate-500 text-sm">10 pitches. Max home runs wins!</p>
              </div>
            </div>
          </button>
          
          <button
            onClick={() => onSelectMode('duel')}
            data-testid="pitcher-vs-batter-btn"
            className="w-full p-5 bg-obsidian border border-cyan-500/20 rounded-xl 
                       hover:border-cyan-500/40 transition-all group text-left"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-cyan-500/10 flex items-center justify-center
                            group-hover:bg-cyan-500/20 transition-colors">
                <Users className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">Pitcher vs Batter</h3>
                <p className="text-slate-500 text-sm">1v1 multiplayer duel. 3 innings!</p>
              </div>
            </div>
          </button>
        </div>
        
        {/* 3D Toggle */}
        <div className="mt-4 pt-4 border-t border-slate-800">
          <button
            onClick={onToggle3D}
            className="w-full flex items-center justify-between p-3 rounded-lg bg-obsidian border border-slate-800 hover:border-cyan-500/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-400">Graphics Mode</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${use3D ? 'text-cyan-400' : 'text-slate-500'}`}>
                {use3D ? '3D Stadium' : '2D Classic'}
              </span>
              <div className={`w-10 h-5 rounded-full transition-colors ${use3D ? 'bg-cyan-600' : 'bg-slate-700'}`}>
                <div className={`w-4 h-4 rounded-full bg-white mt-0.5 transition-transform ${use3D ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </div>
          </button>
        </div>
        
        {/* Quick actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onShowLeaderboard}
            data-testid="leaderboard-btn"
            className="flex-1 p-3 bg-obsidian border border-slate-800 rounded-lg
                       hover:border-amber-500/30 transition-all flex items-center justify-center gap-2"
          >
            <Medal className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-slate-300">Leaderboard</span>
          </button>
          <button
            onClick={onShowAchievements}
            data-testid="achievements-btn"
            className="flex-1 p-3 bg-obsidian border border-slate-800 rounded-lg
                       hover:border-purple-500/30 transition-all flex items-center justify-center gap-2"
          >
            <Award className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-slate-300">Achievements</span>
          </button>
        </div>
        
        {/* Rank display */}
        <div className="mt-6 pt-6 border-t border-slate-800">
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Your Rank</span>
            <span className="font-semibold flex items-center gap-1" style={{ color: rank.color }}>
              {rank.icon} {rank.name} ({playerStats?.rankedPoints || 0} pts)
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ==================== Home Run Derby Mode ====================

const HomeRunDerby = ({ onShowLeaderboard }) => {
  const navigate = useNavigate();
  const {
    derbyPitchesLeft,
    derbyHomeRuns,
    derbyTotalDistance,
    derbyLongestHR,
    gameStatus,
    currentPitch,
    pitchInFlight,
    swingResult,
    showResult,
    throwDerbyPitch,
    swingBat,
    saveDerbyScore,
    resetGame
  } = useBaseballStore();
  
  const [ballPosition, setBallPosition] = useState([0, 2.5, -60]);
  const [ballTrail, setBallTrail] = useState([]);
  const [swingAngle, setSwingAngle] = useState(25);
  const [isSwinging, setIsSwinging] = useState(false);
  const [isPitcherThrowing, setIsPitcherThrowing] = useState(false);
  const [pitchStartTime, setPitchStartTime] = useState(null);
  const [showStatcast, setShowStatcast] = useState(false);
  
  // Live tracking stats during ball flight
  const [liveStats, setLiveStats] = useState({
    distance: 0,
    height: 0,
    peakHeight: 0,
    exitVelocity: 0,
    launchAngle: 0
  });
  const [isTracking, setIsTracking] = useState(false);
  
  const animationRef = useRef(null);
  const peakHeightRef = useRef(0);
  
  // Ball animation with trail - Updated for new coordinate system
  useEffect(() => {
    if (pitchInFlight && currentPitch) {
      const startTime = Date.now();
      setPitchStartTime(startTime);
      setShowStatcast(false);
      setIsTracking(false);
      setBallTrail([]);
      setIsPitcherThrowing(true);
      peakHeightRef.current = 0;
      
      const startZ = -60; // Pitcher distance
      const endZ = 0;     // Home plate
      const duration = 800; // Slightly longer for better animation
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Ease out for more realistic ball approach
        const easeProgress = 1 - Math.pow(1 - progress, 2);
        const z = startZ + (endZ - startZ) * easeProgress;
        
        const movement = PITCH_TYPES[currentPitch.type]?.movement || { x: 0, y: 0 };
        const moveX = movement.x === 'random' ? (Math.random() - 0.5) * 0.3 : movement.x * progress * 0.5;
        const moveY = movement.y === 'random' ? (Math.random() - 0.5) * 0.2 : movement.y * progress * 0.3;
        const x = (currentPitch.targetX || 0) * 0.5 + moveX;
        const y = 2.5 + moveY;
        
        setBallPosition([x, y, z]);
        setBallTrail(prev => [...prev.slice(-20), { x, y, z }]);
        
        // Stop pitcher throwing animation midway
        if (progress > 0.3) setIsPitcherThrowing(false);
        
        if (progress < 1 && pitchInFlight) {
          animationRef.current = requestAnimationFrame(animate);
        }
      };
      
      animationRef.current = requestAnimationFrame(animate);
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }
  }, [pitchInFlight, currentPitch]);
  
  // Bat crack sound effect
  const playBatCrack = useCallback((contactQuality) => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create oscillator for the initial crack
      const osc = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      const filter = audioCtx.createBiquadFilter();
      
      // Create noise for the crack texture
      const noiseBuffer = audioCtx.createBuffer(1, audioCtx.sampleRate * 0.15, audioCtx.sampleRate);
      const noiseData = noiseBuffer.getChannelData(0);
      for (let i = 0; i < noiseData.length; i++) {
        noiseData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / noiseData.length, 2);
      }
      
      const noiseSource = audioCtx.createBufferSource();
      noiseSource.buffer = noiseBuffer;
      
      const noiseGain = audioCtx.createGain();
      const noiseFilter = audioCtx.createBiquadFilter();
      
      // Configure based on contact quality
      const volume = contactQuality === 'perfect' ? 0.8 : contactQuality === 'good' ? 0.6 : 0.4;
      const freq = contactQuality === 'perfect' ? 800 : contactQuality === 'good' ? 600 : 400;
      
      // Oscillator settings (the "ping" of the bat)
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, audioCtx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(200, audioCtx.currentTime + 0.1);
      
      filter.type = 'highpass';
      filter.frequency.value = 200;
      
      gainNode.gain.setValueAtTime(volume, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.15);
      
      // Noise settings (the "crack" texture)
      noiseFilter.type = 'bandpass';
      noiseFilter.frequency.value = 2000;
      noiseFilter.Q.value = 1;
      
      noiseGain.gain.setValueAtTime(volume * 0.8, audioCtx.currentTime);
      noiseGain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.1);
      
      // Connect oscillator
      osc.connect(filter);
      filter.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      // Connect noise
      noiseSource.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(audioCtx.destination);
      
      // Play
      osc.start();
      noiseSource.start();
      osc.stop(audioCtx.currentTime + 0.2);
      noiseSource.stop(audioCtx.currentTime + 0.15);
      
      // Cleanup
      setTimeout(() => audioCtx.close(), 300);
    } catch (e) {
      console.log('Audio not supported');
    }
  }, []);

  // Spray chart data - track all hits
  const [sprayChartHits, setSprayChartHits] = useState([]);

  // Hit ball animation - FASTER with HIGHER arc
  useEffect(() => {
    if (showResult && swingResult && swingResult.distance > 0) {
      setShowStatcast(true);
      setIsTracking(true);
      peakHeightRef.current = 0;
      
      // Play bat crack sound
      playBatCrack(swingResult.contactQuality);
      
      // Add to spray chart
      const hitAngle = (Math.random() - 0.5) * 1.2; // -0.6 to 0.6 radians spread
      const hitDistance = swingResult.distance;
      setSprayChartHits(prev => [...prev.slice(-19), { 
        angle: hitAngle, 
        distance: hitDistance, 
        result: swingResult.result,
        exitVelo: swingResult.exitVelocity
      }]);
      
      const startPos = [...ballPosition];
      const finalDistance = swingResult.distance;
      const launchAngle = swingResult.launchAngle || 25;
      const exitVelocity = swingResult.exitVelocity || 100;
      const isHR = swingResult.result === 'HOMERUN';
      
      // FASTER animation - 3 seconds for HR, proportional for others
      const duration = isHR ? 3000 : Math.max(1500, finalDistance * 4);
      const startTime = Date.now();
      
      // Horizontal direction based on spray angle
      const horizAngle = hitAngle * 0.5;
      
      // HIGHER peak height for better visibility
      // MLB HR peak heights are 60-130 ft, scale accordingly
      const maxPeakHeight = Math.max(8, (launchAngle / 12) * (finalDistance / 40));
      
      const animateHit = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Smooth easing for ball flight
        const easeProgress = progress < 0.5 
          ? 2 * progress * progress 
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        // Ball flies toward outfield (negative z)
        const maxZ = isHR ? -80 : -(finalDistance / 5);
        const z = startPos[2] + maxZ * easeProgress;
        
        // Parabolic height - peaks at 45% of flight, HIGHER arc
        const heightProgress = Math.sin(progress * Math.PI);
        const currentHeight = maxPeakHeight * heightProgress;
        const y = startPos[1] + currentHeight;
        
        // Track peak height
        if (currentHeight > peakHeightRef.current) {
          peakHeightRef.current = currentHeight;
        }
        
        // Horizontal spread
        const x = startPos[0] + horizAngle * easeProgress * 30;
        
        setBallPosition([x, Math.max(0.5, y), z]);
        setBallTrail(prev => [...prev.slice(-40), { x, y: Math.max(0.5, y), z }]);
        
        // Update live stats during flight
        const currentDistance = Math.round(finalDistance * easeProgress);
        // Height scale: convert internal units to feet
        const displayHeight = Math.round(currentHeight * 12);
        const displayPeakHeight = Math.round(peakHeightRef.current * 12);
        
        setLiveStats({
          distance: currentDistance,
          height: displayHeight,
          peakHeight: displayPeakHeight,
          exitVelocity: exitVelocity,
          launchAngle: launchAngle
        });
        
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animateHit);
        } else {
          // Animation complete
          setIsTracking(false);
          setLiveStats(prev => ({
            ...prev,
            distance: finalDistance,
            height: 0 // Ball landed
          }));
        }
      };
      
      animationRef.current = requestAnimationFrame(animateHit);
    }
  }, [showResult, swingResult]);
  
  const handlePitch = useCallback(() => {
    if (gameStatus !== 'playing' || pitchInFlight || derbyPitchesLeft <= 0) return;
    setBallTrail([]);
    setBallPosition([0, 2.5, -60]);
    setLiveStats({ distance: 0, height: 0, peakHeight: 0, exitVelocity: 0, launchAngle: 0 });
    throwDerbyPitch();
  }, [gameStatus, pitchInFlight, derbyPitchesLeft, throwDerbyPitch]);
  
  const handleSwing = useCallback(() => {
    if (!pitchInFlight || isSwinging) return;
    
    setIsSwinging(true);
    
    const pitchDuration = 800;
    const elapsed = Date.now() - pitchStartTime;
    const optimalTime = pitchDuration * 0.85;
    const timing = elapsed - optimalTime;
    
    swingBat(timing, swingAngle);
    
    setTimeout(() => setIsSwinging(false), 400);
  }, [pitchInFlight, pitchStartTime, swingAngle, swingBat, isSwinging]);
  
  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        if (!pitchInFlight && gameStatus === 'playing' && derbyPitchesLeft > 0) {
          handlePitch();
        } else if (pitchInFlight) {
          handleSwing();
        }
      }
      if (e.code === 'ArrowUp') setSwingAngle(prev => Math.min(prev + 5, 45));
      if (e.code === 'ArrowDown') setSwingAngle(prev => Math.max(prev - 5, -10));
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pitchInFlight, gameStatus, derbyPitchesLeft, handlePitch, handleSwing]);
  
  useEffect(() => {
    if (gameStatus === 'ended') {
      saveDerbyScore();
    }
  }, [gameStatus, saveDerbyScore]);
  
  return (
    <div className="relative w-full h-full">
      <BaseballField2D
        ballPosition={{ x: ballPosition[0], y: ballPosition[1], z: ballPosition[2] }}
        ballVisible={pitchInFlight || showResult}
        showStrikeZone={!showResult && !pitchInFlight}
        batterSwinging={isSwinging}
        hitResult={swingResult?.result}
        ballTrail={ballTrail}
        pitcherThrowing={isPitcherThrowing}
      />
      
      {/* LIVE Statcast HUD - Right side, doesn't block ball flight */}
      <AnimatePresence>
        {showStatcast && swingResult && swingResult.distance > 0 && (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            className="absolute top-28 right-4 bg-black/95 backdrop-blur-md rounded-xl p-4 text-white w-[200px] border border-cyan-500/40 z-20"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-2 h-2 rounded-full ${isTracking ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`} />
              <span className="text-cyan-400 text-[10px] font-mono tracking-widest">
                {isTracking ? 'TRACKING' : 'FINAL'}
              </span>
            </div>
            
            <div className="space-y-3">
              {/* Distance - Live updating */}
              <div>
                <div className="text-gray-500 text-[9px] uppercase tracking-wider">Distance</div>
                <div className="text-3xl font-black font-mono text-amber-400 tabular-nums">
                  {liveStats.distance || swingResult.distance}
                  <span className="text-xs text-gray-500 ml-1">FT</span>
                </div>
              </div>
              
              {/* Height - Live updating */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <div className="text-gray-500 text-[9px] uppercase tracking-wider">Height</div>
                  <div className="text-xl font-bold font-mono text-cyan-400 tabular-nums">
                    {liveStats.height}
                    <span className="text-[10px] text-gray-500 ml-0.5">FT</span>
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-[9px] uppercase tracking-wider">Peak</div>
                  <div className="text-xl font-bold font-mono text-emerald-400 tabular-nums">
                    {liveStats.peakHeight}
                    <span className="text-[10px] text-gray-500 ml-0.5">FT</span>
                  </div>
                </div>
              </div>
              
              {/* Exit Velo & Launch Angle */}
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-800">
                <div>
                  <div className="text-gray-500 text-[9px] uppercase tracking-wider">Exit Velo</div>
                  <div className="text-lg font-bold font-mono">
                    {swingResult.exitVelocity}
                    <span className="text-[10px] text-gray-500 ml-0.5">MPH</span>
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 text-[9px] uppercase tracking-wider">Launch</div>
                  <div className="text-lg font-bold font-mono">
                    {swingResult.launchAngle}
                    <span className="text-[10px] text-gray-500 ml-0.5">°</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Score HUD */}
      <div className="absolute top-4 left-4 right-4 flex justify-between items-start z-10 pointer-events-none">
        <div className="bg-black/80 backdrop-blur-sm rounded-xl p-4 pointer-events-auto border border-amber-500/20">
          <div className="text-amber-400 text-xs font-mono tracking-widest mb-2">HOME RUN DERBY</div>
          <div className="grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-white font-mono">{derbyPitchesLeft}</div>
              <div className="text-[10px] text-gray-500 uppercase">Pitches</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-amber-400 font-mono">{derbyHomeRuns}</div>
              <div className="text-[10px] text-gray-500 uppercase">Home Runs</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-cyan-400 font-mono">{derbyTotalDistance}</div>
              <div className="text-[10px] text-gray-500 uppercase">Total Ft</div>
            </div>
          </div>
        </div>
        
        {/* Spray Chart */}
        <div className="bg-black/80 backdrop-blur-sm rounded-xl p-3 pointer-events-auto border border-emerald-500/20">
          <div className="text-emerald-400 text-[10px] font-mono tracking-widest mb-2 text-center">SPRAY CHART</div>
          <div className="relative w-[120px] h-[80px]">
            {/* Mini field outline */}
            <svg viewBox="0 0 120 80" className="w-full h-full">
              {/* Outfield arc */}
              <path 
                d="M 10 75 Q 60 10 110 75" 
                fill="none" 
                stroke="#166534" 
                strokeWidth="2"
              />
              {/* Foul lines */}
              <line x1="60" y1="75" x2="10" y2="40" stroke="#374151" strokeWidth="1" />
              <line x1="60" y1="75" x2="110" y2="40" stroke="#374151" strokeWidth="1" />
              {/* Infield */}
              <path 
                d="M 40 65 L 60 50 L 80 65 L 60 75 Z" 
                fill="none" 
                stroke="#854d0e" 
                strokeWidth="1"
              />
              {/* Wall markers */}
              <text x="15" y="38" fill="#fbbf24" fontSize="6">330</text>
              <text x="55" y="18" fill="#fbbf24" fontSize="6">400</text>
              <text x="95" y="38" fill="#fbbf24" fontSize="6">330</text>
              
              {/* Hit markers */}
              {sprayChartHits.map((hit, idx) => {
                // Convert hit angle and distance to x,y coordinates
                const maxDist = 500;
                const normalizedDist = Math.min(hit.distance / maxDist, 1);
                const distFromHome = 15 + normalizedDist * 50; // 15 to 65 units from home
                const x = 60 + Math.sin(hit.angle) * distFromHome;
                const y = 75 - Math.cos(hit.angle * 0.7) * distFromHome;
                
                const color = hit.result === 'HOMERUN' ? '#fbbf24' : 
                              hit.result === 'TRIPLE' ? '#22c55e' :
                              hit.result === 'DOUBLE' ? '#3b82f6' :
                              hit.result === 'SINGLE' ? '#a855f7' : '#6b7280';
                
                return (
                  <circle 
                    key={idx} 
                    cx={x} 
                    cy={y} 
                    r={idx === sprayChartHits.length - 1 ? 4 : 2.5}
                    fill={color}
                    opacity={0.4 + (idx / sprayChartHits.length) * 0.6}
                    stroke={idx === sprayChartHits.length - 1 ? '#fff' : 'none'}
                    strokeWidth="1"
                  />
                );
              })}
              
              {/* Home plate */}
              <polygon points="60,75 56,72 56,70 64,70 64,72" fill="#fff" />
            </svg>
          </div>
          {/* Legend */}
          <div className="flex justify-center gap-2 mt-1">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-[8px] text-gray-400">HR</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-500" />
              <span className="text-[8px] text-gray-400">3B</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-blue-500" />
              <span className="text-[8px] text-gray-400">2B</span>
            </div>
          </div>
        </div>
        
        <div className="bg-black/80 backdrop-blur-sm rounded-xl p-4 pointer-events-auto border border-slate-700">
          <div className="text-gray-500 text-xs mb-2">Swing Angle</div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setSwingAngle(prev => Math.max(prev - 5, -10))}
              className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-lg font-bold transition-colors"
            >−</button>
            <div className="text-2xl font-bold text-white w-14 text-center font-mono">{swingAngle}°</div>
            <button 
              onClick={() => setSwingAngle(prev => Math.min(prev + 5, 45))}
              className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-lg font-bold transition-colors"
            >+</button>
          </div>
        </div>
      </div>
      
      {/* Result badge - Bottom left, doesn't block ball flight */}
      <AnimatePresence>
        {showResult && swingResult && (
          <motion.div
            initial={{ scale: 0, opacity: 0, x: -50 }}
            animate={{ scale: 1, opacity: 1, x: 0 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="absolute bottom-24 left-4 pointer-events-none z-20"
          >
            <div className={`
              px-6 py-4 rounded-xl shadow-2xl border-2
              ${swingResult.result === 'HOMERUN' ? 'bg-gradient-to-r from-amber-500 to-orange-500 border-amber-300' : 
               swingResult.result === 'STRIKE' ? 'bg-gradient-to-r from-red-600 to-red-700 border-red-400' : 
               swingResult.result === 'OUT' ? 'bg-gradient-to-r from-gray-600 to-gray-700 border-gray-400' :
               'bg-gradient-to-r from-green-500 to-emerald-500 border-green-300'}
            `}>
              <div className="text-3xl font-black text-white drop-shadow-lg">
                {swingResult.result === 'HOMERUN' ? '💣 HOME RUN!' : 
                 swingResult.result === 'STRIKE' ? 'STRIKE!' : 
                 swingResult.result === 'OUT' ? 'OUT!' :
                 swingResult.result}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Controls */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4 z-10">
        {gameStatus === 'playing' && !pitchInFlight && derbyPitchesLeft > 0 && (
          <Button
            size="lg"
            onClick={handlePitch}
            onTouchEnd={(e) => { e.preventDefault(); handlePitch(); }}
            data-testid="pitch-btn"
            className="bg-cyan-600 hover:bg-cyan-500 active:bg-cyan-400 text-white px-10 py-7 text-xl font-bold touch-manipulation shadow-lg shadow-cyan-500/30"
          >
            <Target className="w-6 h-6 mr-2" />
            Pitch (Space)
          </Button>
        )}
        
        {pitchInFlight && (
          <Button
            size="lg"
            onClick={handleSwing}
            onTouchEnd={(e) => { e.preventDefault(); handleSwing(); }}
            data-testid="swing-btn"
            className="bg-amber-500 hover:bg-amber-400 active:bg-amber-300 text-black px-14 py-10 text-2xl font-black animate-pulse touch-manipulation shadow-lg shadow-amber-500/40"
          >
            <Zap className="w-7 h-7 mr-2" />
            SWING!
          </Button>
        )}
        
        <div className="text-gray-400 text-sm bg-black/50 px-4 py-2 rounded-full">
          <span className="text-cyan-400">↑↓</span> Adjust angle • <span className="text-amber-400">Space</span> to pitch/swing
        </div>
      </div>
      
      {/* Game Over */}
      {gameStatus === 'ended' && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm z-30">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-slate-900 border border-amber-500/30 rounded-2xl p-10 text-center max-w-md"
          >
            <Trophy className="w-20 h-20 text-amber-400 mx-auto mb-4" />
            <h2 className="text-4xl font-black text-white mb-2">Derby Complete!</h2>
            <div className="text-6xl font-black text-amber-400 mb-2 font-mono">{derbyHomeRuns} HR</div>
            <div className="text-xl text-gray-400 mb-6">
              Total: {derbyTotalDistance} ft • Longest: {derbyLongestHR || 0} ft
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button 
                variant="outline" 
                onClick={onShowLeaderboard} 
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              >
                <Medal className="w-4 h-4 mr-2" />
                Leaderboard
              </Button>
              <Button
                onClick={() => {
                  resetGame();
                  useBaseballStore.getState().startDerby();
                }}
                className="bg-amber-600 hover:bg-amber-500 text-white"
              >
                <Target className="w-4 h-4 mr-2" />
                Play Again
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

// ==================== Main Baseball Game Component ====================

const BaseballGame = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { gameMode, gameStatus, startDerby, resetGame, loadPlayerStats, playerStats, achievements } = useBaseballStore();
  
  const [showModeSelect, setShowModeSelect] = useState(true);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showMultiplayerLobby, setShowMultiplayerLobby] = useState(false);
  const [multiplayerGame, setMultiplayerGame] = useState(null);
  
  useEffect(() => {
    if (isAuthenticated) {
      loadPlayerStats();
    }
    
    // Check for room code in URL
    const roomCode = searchParams.get('room');
    if (roomCode) {
      setShowMultiplayerLobby(true);
    }
  }, [isAuthenticated, loadPlayerStats, searchParams]);
  
  const [use3D, setUse3D] = useState(false); // Default to 2D mode (more reliable)
  
  const handleSelectMode = (mode) => {
    if (mode === 'derby') {
      setShowModeSelect(false);
      startDerby();
    } else if (mode === 'duel') {
      setShowMultiplayerLobby(true);
    }
  };
  
  const handleDerbyEnd = async (results) => {
    // Save derby results
    if (isAuthenticated && results.homeRuns > 0) {
      try {
        await fetch(`${API_URL}/api/baseball/derby-score`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            home_runs: results.homeRuns,
            total_distance: results.totalDistance,
            longest_hr: results.longestHR,
          }),
        });
      } catch (error) {
        console.error('Failed to save derby results:', error);
      }
    }
    
    // Reload stats
    loadPlayerStats();
  };
  
  const handleExitDerby = () => {
    resetGame();
    setShowModeSelect(true);
  };
  
  const handleJoinRoom = (roomId, role, roomData) => {
    setShowMultiplayerLobby(false);
    setShowModeSelect(false);
    setMultiplayerGame({ roomId, role, opponentName: roomData.host_name || roomData.guest_name });
  };
  
  const handleBack = () => {
    resetGame();
    setMultiplayerGame(null);
    setShowModeSelect(true);
    navigate('/dashboard');
  };
  
  const handleExitMultiplayer = () => {
    setMultiplayerGame(null);
    setShowModeSelect(true);
  };
  
  return (
    <div className="h-screen w-screen bg-slate-950 overflow-hidden">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-30 bg-gradient-to-b from-black/80 to-transparent py-3 px-4">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="text-gray-300 hover:text-white"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Back
          </Button>
          
          <div className="flex items-center gap-2">
            <span className="text-2xl">⚾</span>
            <span className="text-white font-bold text-lg">Baseball</span>
          </div>
          
          <div className="w-20" />
        </div>
      </header>
      
      {/* Game Content */}
      <div className="h-full w-full pt-14">
        {multiplayerGame ? (
          <PitcherVsBatter
            roomId={multiplayerGame.roomId}
            role={multiplayerGame.role}
            opponentName={multiplayerGame.opponentName}
            onExit={handleExitMultiplayer}
          />
        ) : showModeSelect ? (
          <div className="h-full relative bg-obsidian">
            <GameModeSelector 
              onSelectMode={handleSelectMode}
              onShowLeaderboard={() => setShowLeaderboard(true)}
              onShowAchievements={() => setShowAchievements(true)}
              playerStats={playerStats}
              use3D={use3D}
              onToggle3D={() => setUse3D(!use3D)}
            />
          </div>
        ) : gameMode === 'derby' ? (
          use3D ? (
            <Suspense fallback={
              <div className="flex items-center justify-center h-full bg-obsidian">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin text-cyan-400 mx-auto mb-4" />
                  <p className="text-white text-lg">Loading 3D Stadium...</p>
                </div>
              </div>
            }>
              <HomeRunDerby3D 
                onExit={handleExitDerby}
                onGameEnd={handleDerbyEnd}
              />
            </Suspense>
          ) : (
            <HomeRunDerby onShowLeaderboard={() => setShowLeaderboard(true)} />
          )
        ) : (
          <div className="flex items-center justify-center h-full text-white">
            <p>Loading...</p>
          </div>
        )}
      </div>
      
      {/* Dialogs */}
      <DerbyLeaderboard 
        isOpen={showLeaderboard} 
        onClose={() => setShowLeaderboard(false)} 
      />
      
      <AchievementsPanel 
        isOpen={showAchievements} 
        onClose={() => setShowAchievements(false)}
        achievements={achievements || []}
        playerStats={playerStats}
      />
      
      <MultiplayerLobby
        isOpen={showMultiplayerLobby}
        onClose={() => setShowMultiplayerLobby(false)}
        onJoinRoom={handleJoinRoom}
      />
    </div>
  );
};

export default BaseballGame;
